import React, { useMemo } from 'react';
import Styles from './InteractiveMap.styled';
import tooltip from '../Tooltip/Tooltip';
import theme from '../../styles/theme';

const InteractiveMap = ({
  selectableStates = [],
  selectedState = null,
  onStateClick = () => {},
  stateTooltipData = [],
  selectableStateColor = theme.colors.babyBlue,
  selectedStateColor = theme.colors.vmfBlue,
  stateBorderColor = theme.colors.white,
  ...props
}) => {
  const { tooltipData } = useMemo(() => {
    const tooltipData = selectableStates.reduce((p, c) => {
      const foundTooltipData = stateTooltipData.find(f => f.state === c);

      if (foundTooltipData && !p[c]) {
        p[c] = {
          text: foundTooltipData.text,
          heading: foundTooltipData.heading,
        };
      }

      return p;
    }, {});

    return {
      generateTooltip: state =>
        tooltipData?.[state] || { text: null, heading: null },
      tooltipData,
    };
  }, [stateTooltipData, selectableStates]);

  return (
    <Styles
      selectableStateColor={selectableStateColor}
      selectedStateColor={selectedStateColor}
      stateBorderColor={stateBorderColor}
    >
      <tooltip.provider
        config={{
          backgroundColor: 'white',
          textColor: 'black',
          textAlign: 'center',
          boxShadow: '1px 2px 8px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div id='us-map'>
          <svg viewBox='0 0 960 600' width='960'>
            <path
              state='Hawaii'
              id='HI'
              stroke='#000000'
              fill='#FFFFFF'
              strokeWidth='0.75'
              d='M233.1,519.3l1.9-3.6l2.3-0.3l0.3,0.8l-2.1,3.1H233.1z M243.3,515.6l6.1,2.6l2.1-0.3l1.6-3.9l-0.6-3.4l-4.2-0.5l-4,1.8L243.3,515.6z M274,525.6l3.7,5.5l2.4-0.3l1.1-0.5l1.5,1.3l3.7-0.2 l1-1.5l-2.9-1.8l-1.9-3.7l-2.1-3.6l-5.8,2.9L274,525.6z M294.2,534.5l1.3-1.9l4.7,1l0.6-0.5l6.1,0.6l-0.3,1.3l-2.6,1.5l-4.4-0.3 L294.2,534.5L294.2,534.5z M299.5,539.7l1.9,3.9l3.1-1.1l0.3-1.6l-1.6-2.1l-3.7-0.3V539.7L299.5,539.7z M306.5,538.5l2.3-2.9 l4.7,2.4l4.4,1.1l4.4,2.7v1.9l-3.6,1.8l-4.8,1l-2.4-1.5L306.5,538.5z M323.1,554.1l1.6-1.3l3.4,1.6l7.6,3.6l3.4,2.1l1.6,2.4l1.9,4.4 l4,2.6l-0.3,1.3l-3.9,3.2l-4.2,1.5l-1.5-0.6l-3.1,1.8l-2.4,3.2l-2.3,2.9l-1.8-0.2l-3.6-2.6l-0.3-4.5l0.6-2.4l-1.6-5.7l-2.1-1.8 l-0.2-2.6l2.3-1l2.1-3.1l0.5-1l-1.6-1.8L323.1,554.1L323.1,554.1z'
              className={
                'map-state' +
                (selectableStates.includes('Hawaii') ? ' selectable' : '') +
                (selectedState === 'Hawaii' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Hawaii']?.text || null}
              data-tooltipheading={tooltipData?.['Hawaii']?.heading || null}
              onClick={() =>
                selectableStates.includes('Hawaii')
                  ? onStateClick('Hawaii')
                  : null
              }
            />
            <path
              state='Alaska'
              id='Alaska'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M158.1,453.7l-0.3,85.4l1.6,1l3.1,0.2l1.5-1.1h2.6l0.2,2.9 l7,6.8l0.5,2.6l3.4-1.9l0.6-0.2l0.3-3.1l1.5-1.6l1.1-0.2l1.9-1.5l3.1,2.1l0.6,2.9l1.9,1.1l1.1,2.4l3.9,1.8l3.4,6l2.7,3.9l2.3,2.7 l1.5,3.7l5,1.8l5.2,2.1l1,4.4l0.5,3.1l-1,3.4l-1.8,2.3l-1.6-0.8l-1.5-3.1l-2.7-1.5l-1.8-1.1l-0.8,0.8l1.5,2.7l0.2,3.7l-1.1,0.5 l-1.9-1.9l-2.1-1.3l0.5,1.6l1.3,1.8l-0.8,0.8c0,0-0.8-0.3-1.3-1c-0.5-0.6-2.1-3.4-2.1-3.4l-1-2.3c0,0-0.3,1.3-1,1 c-0.6-0.3-1.3-1.5-1.3-1.5l1.8-1.9l-1.5-1.5v-5h-0.8l-0.8,3.4l-1.1,0.5l-1-3.7l-0.6-3.7l-0.8-0.5l0.3,5.7v1.1l-1.5-1.3l-3.6-6 l-2.1-0.5l-0.6-3.7l-1.6-2.9l-1.6-1.1v-2.3l2.1-1.3l-0.5-0.3l-2.6,0.6l-3.4-2.4l-2.6-2.9l-4.8-2.6l-4-2.6l1.3-3.2v-1.6l-1.8,1.6 l-2.9,1.1l-3.7-1.1l-5.7-2.4h-5.5l-0.6,0.5l-6.5-3.9l-2.1-0.3l-2.7-5.8l-3.6,0.3l-3.6,1.5l0.5,4.5l1.1-2.9l1,0.3l-1.5,4.4l3.2-2.7 l0.6,1.6l-3.9,4.4l-1.3-0.3l-0.5-1.9l-1.3-0.8l-1.3,1.1l-2.7-1.8l-3.1,2.1l-1.8,2.1l-3.4,2.1l-4.7-0.2l-0.5-2.1l3.7-0.6v-1.3 l-2.3-0.6l1-2.4l2.3-3.9v-1.8l0.2-0.8l4.4-2.3l1,1.3h2.7l-1.3-2.6l-3.7-0.3l-5,2.7l-2.4,3.4l-1.8,2.6l-1.1,2.3l-4.2,1.5l-3.1,2.6 l-0.3,1.6l2.3,1l0.8,2.1l-2.7,3.2l-6.5,4.2l-7.8,4.2l-2.1,1.1l-5.3,1.1l-5.3,2.3l1.8,1.3l-1.5,1.5l-0.5,1.1l-2.7-1l-3.2,0.2 l-0.8,2.3h-1l0.3-2.4l-3.6,1.3l-2.9,1l-3.4-1.3l-2.9,1.9h-3.2l-2.1,1.3l-1.6,0.8l-2.1-0.3l-2.6-1.1l-2.3,0.6l-1,1l-1.6-1.1v-1.9 l3.1-1.3l6.3,0.6l4.4-1.6l2.1-2.1l2.9-0.6l1.8-0.8l2.7,0.2l1.6,1.3l1-0.3l2.3-2.7l3.1-1l3.4-0.6l1.3-0.3l0.6,0.5h0.8l1.3-3.7l4-1.5 l1.9-3.7l2.3-4.5l1.6-1.5l0.3-2.6l-1.6,1.3l-3.4,0.6l-0.6-2.4l-1.3-0.3l-1,1l-0.2,2.9l-1.5-0.2l-1.5-5.8l-1.3,1.3l-1.1-0.5l-0.3-1.9 l-4,0.2l-2.1,1.1l-2.6-0.3l1.5-1.5l0.5-2.6l-0.6-1.9l1.5-1l1.3-0.2l-0.6-1.8v-4.4l-1-1l-0.8,1.5h-6.1l-1.5-1.3l-0.6-3.9l-2.1-3.6v-1 l2.1-0.8L53,514l1.1-1.1l-0.8-0.5l-1.3,0.5l-1.1-2.7l1-5l4.5-3.2l2.6-1.6l1.9-3.7l2.7-1.3l2.6,1.1l0.3,2.4l2.4-0.3l3.2-2.4l1.6,0.6 l1,0.6h1.6l2.3-1.3l0.8-4.4c0,0,0.3-2.9,1-3.4c0.6-0.5,1-1,1-1l-1.1-1.9l-2.6,0.8l-3.2,0.8l-1.9-0.5l-3.6-1.8l-5-0.2l-3.6-3.7 l0.5-3.9l0.6-2.4l-2.1-1.8l-1.9-3.7l0.5-0.8l6.8-0.5h2.1l1,1h0.6l-0.2-1.6l3.9-0.6l2.6,0.3l1.5,1.1l-1.5,2.1l-0.5,1.5l2.7,1.6l5,1.8 l1.8-1l-2.3-4.4l-1-3.2l1-0.8l-3.4-1.9l-0.5-1.1l0.5-1.6l-0.8-3.9l-2.9-4.7l-2.4-4.2l2.9-1.9h3.2l1.8,0.6l4.2-0.2l3.7-3.6l1.1-3.1 l3.7-2.4l1.6,1l2.7-0.6l3.7-2.1l1.1-0.2l1,0.8l4.5-0.2l2.7-3.1h1.1l3.6,2.4l1.9,2.1l-0.5,1.1l0.6,1.1l1.6-1.6l3.9,0.3l0.3,3.7 l1.9,1.5l7.1,0.6l6.3,4.2l1.5-1l5.2,2.6l2.1-0.6l1.9-0.8l4.8,1.9L158.1,453.7L158.1,453.7z M43,482.6l2.1,5.3l-0.2,1l-2.9-0.3 l-1.8-4l-1.8-1.5H36l-0.2-2.6l1.8-2.4l1.1,2.4l1.5,1.5L43,482.6z M40.4,516.1l3.7,0.8l3.7,1l0.8,1l-1.6,3.7l-3.1-0.2l-3.4-3.6 L40.4,516.1z M19.7,502l1.1,2.6l1.1,1.6l-1.1,0.8l-2.1-3.1V502H19.7L19.7,502z M6,575.1l3.4-2.3l3.4-1l2.6,0.3l0.5,1.6l1.9,0.5 l1.9-1.9l-0.3-1.6l2.7-0.6l2.9,2.6l-1.1,1.8l-4.4,1.1l-2.7-0.5l-3.7-1.1l-4.4,1.5l-1.6,0.3L6,575.1L6,575.1z M54.9,570.6l1.6,1.9 l2.1-1.6l-1.5-1.3L54.9,570.6z M57.8,573.6l1.1-2.3l2.1,0.3l-0.8,1.9H57.8z M81.4,571.7l1.5,1.8l1-1.1l-0.8-1.9L81.4,571.7z M90.2,559.2l1.1,5.8l2.9,0.8l5-2.9l4.4-2.6l-1.6-2.4l0.5-2.4l-2.1,1.3l-2.9-0.8l1.6-1.1l1.9,0.8l3.9-1.8l0.5-1.5l-2.4-0.8l0.8-1.9 l-2.7,1.9l-4.7,3.6l-4.9,2.9L90.2,559.2L90.2,559.2z M132.5,539.4l2.4-1.5l-1-1.8l-1.8,1L132.5,539.4z'
              className={
                'map-state' +
                (selectableStates.includes('Alaska') ? ' selectable' : '') +
                (selectedState === 'Alaska' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Alaska']?.text || null}
              data-tooltipheading={tooltipData?.['Alaska']?.heading || null}
              onClick={() =>
                selectableStates.includes('Alaska')
                  ? onStateClick('Alaska')
                  : null
              }
            />
            <path
              state='Florida'
              id='Florida'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M759.8,439.1l2.3,7.3l3.7,9.7l5.3,9.4l3.7,6.3l4.8,5.5l4,3.7 l1.6,2.9l-1.1,1.3l-0.8,1.3l2.9,7.4l2.9,2.9l2.6,5.3l3.6,5.8l4.5,8.2l1.3,7.6l0.5,12l0.6,1.8l-0.3,3.4l-2.4,1.3l0.3,1.9l-0.6,1.9 l0.3,2.4l0.5,1.9l-2.7,3.2l-3.1,1.5l-3.9,0.2l-1.5,1.6l-2.4,1l-1.3-0.5l-1.1-1l-0.3-2.9l-0.8-3.4l-3.4-5.2l-3.6-2.3l-3.9-0.3 l-0.8,1.3l-3.1-4.4l-0.6-3.6l-2.6-4l-1.8-1.1l-1.6,2.1l-1.8-0.3l-2.1-5l-2.9-3.9l-2.9-5.3l-2.6-3.1l-3.6-3.7l2.1-2.4l3.2-5.5 l-0.2-1.6l-4.5-1l-1.6,0.6l0.3,0.6l2.6,1l-1.5,4.5l-0.8,0.5l-1.8-4l-1.3-4.8l-0.3-2.7l1.5-4.7v-9.5l-3.1-3.7l-1.3-3.1l-5.2-1.3 l-1.9-0.6l-1.6-2.6l-3.4-1.6l-1.1-3.4l-2.7-1l-2.4-3.7l-4.2-1.5l-2.9-1.5H711l-4,0.8l-0.2,1.9l0.8,1l-0.5,1.1l-3.1-0.2l-3.7,3.6 l-3.6,1.9h-3.9l-3.2,1.3l-0.3-2.7l-1.6-1.9l-2.9-1.1l-1.6-1.5l-8.1-3.9l-7.6-1.8l-4.4,0.6l-6,0.5l-6,2.1l-3.5,0.6l-0.2-8l-2.6-1.9 l-1.8-1.8l0.3-3.1l10.2-1.3l25.5-2.9l6.8-0.6l5.4,0.3l2.6,3.9l1.5,1.5l8.1,0.5l10.8-0.6l21.5-1.3l5.4-0.7l5.1,0.2l0.4,2.9l2.2,0.8 l0.2-4.6l-1.5-4.2l1.3-1.4l5.6,0.5L759.8,439.1L759.8,439.1z M772.4,571.5l2.4-0.6l1.3-0.2l1.5-2.3l2.3-1.6l1.3,0.5l1.7,0.3l0.4,1.1 l-3.5,1.2l-4.2,1.5l-2.3,1.2L772.4,571.5L772.4,571.5z M785.9,566.5l1.2,1.1l2.7-2.1l5.3-4.2l3.7-3.9l2.5-6.6l1-1.7l0.2-3.4 l-0.7,0.5l-1,2.8l-1.5,4.6l-3.2,5.3l-4.4,4.2l-3.4,1.9L785.9,566.5L785.9,566.5z'
              className={
                'map-state' +
                (selectableStates.includes('Florida') ? ' selectable' : '') +
                (selectedState === 'Florida' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Florida']?.text || null}
              data-tooltipheading={tooltipData?.['Florida']?.heading || null}
              onClick={() =>
                selectableStates.includes('Florida')
                  ? onStateClick('Florida')
                  : null
              }
            />
            <path
              state='New Hampshire'
              id='NH'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M880.8,142.4l0.9-1.1l1.1-3.3l-2.5-0.9l-0.5-3.1l-3.9-1.1 l-0.3-2.7l-7.3-23.4l-4.6-14.5l-0.9,0l-0.6,1.6l-0.6-0.5l-1-1l-1.5,1.9l0,5l0.3,5.7l1.9,2.7v4l-3.7,5.1l-2.6,1.1v1.1l1.1,1.8v8.6 l-0.8,9.2l-0.2,4.8l1,1.3l-0.2,4.5l-0.5,1.8l1,0.7l16.8-4.4l2.2-0.6l1.8-2.8L880.8,142.4L880.8,142.4z'
              className={
                'map-state' +
                (selectableStates.includes('New Hampshire')
                  ? ' selectable'
                  : '') +
                (selectedState === 'New Hampshire' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['New Hampshire']?.text || null}
              data-tooltipheading={
                tooltipData?.['New Hampshire']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('New Hampshire')
                  ? onStateClick('New Hampshire')
                  : null
              }
            />
            <g className='michigan'>
              <path
                state='Michigan'
                id='MI'
                fill='#FFFFFF'
                stroke='#000000'
                strokeWidth='0.75'
                d='M697.9,177.2l-3.2-8.2l-2.3-9.1l-2.4-3.2l-2.6-1.8l-1.6,1.1  l-3.9,1.8l-1.9,5l-2.7,3.7l-1.1,0.6l-1.5-0.6c0,0-2.6-1.5-2.4-2.1c0.2-0.6,0.5-5,0.5-5l3.4-1.3l0.8-3.4l0.6-2.6l2.4-1.6l-0.3-10  l-1.6-2.3l-1.3-0.8l-0.8-2.1l0.8-0.8l1.6,0.3l0.2-1.6L676,131l-1.3-2.6h-2.6l-4.5-1.5l-5.5-3.4h-2.7l-0.6,0.6l-1-0.5l-3.1-2.3  l-2.9,1.8l-2.9,2.3l0.3,3.6l1,0.3l2.1,0.5l0.5,0.8l-2.6,0.8l-2.6,0.3l-1.5,1.8l-0.3,2.1l0.3,1.6l0.3,5.5l-3.6,2.1l-0.6-0.2v-4.2  l1.3-2.4l0.6-2.4l-0.8-0.8l-1.9,0.8l-1,4.2l-2.7,1.1l-1.8,1.9l-0.2,1l0.6,0.8l-0.6,2.6l-2.3,0.5v1.1l0.8,2.4l-1.1,6.1l-1.6,4  l0.6,4.7l0.5,1.1l-0.8,2.4l-0.3,0.8l-0.3,2.7l3.6,6l2.9,6.5l1.5,4.9l-0.8,4.7l-1,6l-2.4,5.2l-0.3,2.7l-3.3,3.1l4.4-0.2l21.4-2.3  l7.3-1l0.1,1.7l6.9-1.2l10.3-1.5l3.9-0.5l0.1-0.6l0.2-1.5l2.1-3.7l2-1.7l-0.2-5.1l1.6-1.6l1.1-0.3l0.2-3.6l1.5-3l1.1,0.6l0.2,0.6  l0.8,0.2l1.9-1L697.9,177.2L697.9,177.2z'
                className={
                  'map-state' +
                  (selectableStates.includes('Michigan') ? ' selectable' : '') +
                  (selectedState === 'Michigan' ? ' selected' : '')
                }
                data-tooltiptext={tooltipData?.['Michigan']?.text || null}
                data-tooltipheading={tooltipData?.['Michigan']?.heading || null}
                onClick={() =>
                  selectableStates.includes('Michigan')
                    ? onStateClick('Michigan')
                    : null
                }
              />
              <path
                state='Michigan'
                id='SP'
                fill='#FFFFFF'
                stroke='#000000'
                strokeWidth='0.75'
                d='M581.6,82.1l1.8-2.1l2.2-0.8l5.4-3.9l2.3-0.6l0.5,0.5  l-5.1,5.1l-3.3,1.9l-2.1,0.9L581.6,82.1L581.6,82.1z M667.8,114.2l0.6,2.5l3.2,0.2l1.3-1.2c0,0-0.1-1.5-0.4-1.6s-1.6-1.9-1.6-1.9  l-2.2,0.2l-1.6,0.2l-0.3,1.1L667.8,114.2L667.8,114.2z M567.5,111.2l0.7-0.6l2.7-0.8l3.6-2.3v-1l0.6-0.6l6-1l2.4-1.9l4.4-2.1  l0.2-1.3l1.9-2.9l1.8-0.8l1.3-1.8l2.3-2.3l4.4-2.4l4.7-0.5l1.1,1.1l-0.3,1l-3.7,1l-1.5,3.1l-2.3,0.8l-0.5,2.4l-2.4,3.2l-0.3,2.6  l0.8,0.5l1-1.1l3.6-2.9l1.3,1.3h2.3l3.2,1l1.5,1.1l1.5,3.1l2.7,2.7l3.9-0.2l1.5-1l1.6,1.3l1.6,0.5l1.3-0.8h1.1l1.6-1l4-3.6l3.4-1.1  l6.6-0.3l4.5-1.9l2.6-1.3l1.5,0.2v5.7l0.5,0.3l2.9,0.8l1.9-0.5l6.1-1.6l1.1-1.1l1.5,0.5v7l3.2,3.1l1.3,0.6l1.3,1l-1.3,0.3l-0.8-0.3  l-3.7-0.5l-2.1,0.6l-2.3-0.2l-3.2,1.5h-1.8l-5.8-1.3l-5.2,0.2l-1.9,2.6l-7,0.6l-2.4,0.8l-1.1,3.1l-1.3,1.1l-0.5-0.2l-1.5-1.6  l-4.5,2.4h-0.6l-1.1-1.6l-0.8,0.2l-1.9,4.4l-1,4l-3.2,7l-1.2-1l-1.4-1l-1.9-10.3l-3.5-1.4l-2.1-2.3l-12.1-2.7l-2.9-1l-8.2-2.2  l-7.9-1.1L567.5,111.2L567.5,111.2z'
                className={
                  'map-state' +
                  (selectableStates.includes('Michigan') ? ' selectable' : '') +
                  (selectedState === 'Michigan' ? ' selected' : '')
                }
                data-tooltiptext={tooltipData?.['Michigan']?.text || null}
                data-tooltipheading={tooltipData?.['Michigan']?.heading || null}
                onClick={() =>
                  selectableStates.includes('Michigan')
                    ? onStateClick('Michigan')
                    : null
                }
              />
            </g>
            <path
              state='Vermont'
              id='VT'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M844.5,154.1l0.3-5.3l-2.9-10.8l-0.6-0.3l-2.9-1.3l0.8-2.9 l-0.8-2.1l-2.7-4.6l1-3.9l-0.8-5.2l-2.4-6.5l-0.8-4.9l26.4-6.7l0.3,5.5l1.9,2.7v4l-3.7,5.1l-2.6,1.1l0,1.1l1.3,1.5l-0.3,8.1 l-0.6,9.3l-0.2,5.6l1,1.3l-0.2,4.6l-0.5,1.7l1,0.7l-7.4,1.5L844.5,154.1L844.5,154.1z'
              className={
                'map-state' +
                (selectableStates.includes('Vermont') ? ' selectable' : '') +
                (selectedState === 'Vermont' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Vermont']?.text || null}
              data-tooltipheading={tooltipData?.['Vermont']?.heading || null}
              onClick={() =>
                selectableStates.includes('Vermont')
                  ? onStateClick('Vermont')
                  : null
              }
            />
            <path
              state='Maine'
              id='ME'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M922.8,78.8l1.9,2.1l2.3,3.7v1.9l-2.1,4.7l-1.9,0.6l-3.4,3.1 l-4.8,5.5c0,0-0.6,0-1.3,0c-0.6,0-1-2.1-1-2.1l-1.8,0.2l-1,1.5l-2.4,1.5l-1,1.5l1.6,1.5l-0.5,0.6l-0.5,2.7l-1.9-0.2V106l-0.3-1.3 l-1.5,0.3l-1.8-3.2l-2.1,1.3l1.3,1.5l0.3,1.1l-0.8,1.3l0.3,3.1l0.2,1.6l-1.6,2.6l-2.9,0.5l-0.3,2.9l-5.3,3.1l-1.3,0.5l-1.6-1.5 l-3.1,3.6l1,3.2l-1.5,1.3l-0.2,4.4l-1.1,6.3l-2.5-1.2l-0.5-3.1l-3.9-1.1l-0.3-2.7l-7.3-23.4l-4.7-14.6l1.4-0.1l1.5,0.4V90l1.3-4.5 l2.6-4.7l1.5-4l-1.9-2.4v-6l0.8-1l0.8-2.7l-0.2-1.5l-0.2-4.8l1.8-4.8l2.9-8.9l2.1-4.2h1.3l1.3,0.2v1.1l1.3,2.3l2.7,0.6l0.8-0.8v-1 l4-2.9l1.8-1.8l1.5,0.2l6,2.4l1.9,1l9.1,29.9h6l0.8,1.9l0.2,4.8l2.9,2.3h0.8l0.2-0.5l-0.5-1.1L922.8,78.8L922.8,78.8z M901.9,109 l1.5-1.5l1.4,1.1l0.6,2.4l-1.7,0.9L901.9,109z M908.6,103.1l1.8,1.9c0,0,1.3,0.1,1.3-0.2s0.2-2,0.2-2l0.9-0.8l-0.8-1.8l-2,0.7 L908.6,103.1z'
              className={
                'map-state' +
                (selectableStates.includes('Maine') ? ' selectable' : '') +
                (selectedState === 'Maine' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Maine']?.text || null}
              data-tooltipheading={tooltipData?.['Maine']?.heading || null}
              onClick={() =>
                selectableStates.includes('Maine')
                  ? onStateClick('Maine')
                  : null
              }
            />
            <path
              state='Rhode Island'
              id='RI'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M874.1,178.9l-3.7-15l6.3-1.8l2.2,1.9l3.3,4.3l2.7,4.4l-3,1.6 l-1.3-0.2l-1.1,1.8l-2.4,1.9L874.1,178.9L874.1,178.9z'
              className={
                'map-state' +
                (selectableStates.includes('Rhode Island')
                  ? ' selectable'
                  : '') +
                (selectedState === 'Rhode Island' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Rhode Island']?.text || null}
              data-tooltipheading={
                tooltipData?.['Rhode Island']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('Rhode Island')
                  ? onStateClick('Rhode Island')
                  : null
              }
            />
            <path
              state='New York'
              id='NY'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M830.4,188.7l-1.1-1l-2.6-0.2l-2.3-1.9l-1.6-6.1l-3.5,0.1 l-2.4-2.7l-19.4,4.4l-43,8.7l-7.5,1.2l-0.7-6.5l1.4-1.1l1.3-1.1l1-1.6l1.8-1.1l1.9-1.8l0.5-1.6l2.1-2.7l1.1-1l-0.2-1l-1.3-3.1 l-1.8-0.2l-1.9-6.1l2.9-1.8l4.4-1.5l4-1.3l3.2-0.5l6.3-0.2l1.9,1.3l1.6,0.2l2.1-1.3l2.6-1.1l5.2-0.5l2.1-1.8l1.8-3.2l1.6-1.9h2.1 l1.9-1.1l0.2-2.3l-1.5-2.1l-0.3-1.5l1.1-2.1v-1.5h-1.8l-1.8-0.8l-0.8-1.1l-0.2-2.6l5.8-5.5l0.6-0.8l1.5-2.9l2.9-4.5l2.7-3.7l2.1-2.4 l2.4-1.8l3.1-1.2l5.5-1.3l3.2,0.2l4.5-1.5l7.6-2.1l0.5,5l2.4,6.5l0.8,5.2l-1,3.9l2.6,4.5l0.8,2.1l-0.8,2.9l2.9,1.3l0.6,0.3l3.1,11 l-0.5,5.1l-0.5,10.8l0.8,5.5l0.8,3.6l1.5,7.3v8.1l-1.1,2.3l1.8,2l0.8,1.7l-1.9,1.8l0.3,1.3l1.3-0.3l1.5-1.3l2.3-2.6l1.1-0.6l1.6,0.6 l2.3,0.2l7.9-3.9l2.9-2.7l1.3-1.5l4.2,1.6l-3.4,3.6L866,194l-7.1,5.3l-2.6,1l-5.8,1.9l-4,1.1l-1.2-0.5l-0.2-3.7l0.5-2.7l-0.2-2.1 l-2.8-1.7l-4.5-1l-3.9-1.1L830.4,188.7L830.4,188.7z'
              className={
                'map-state' +
                (selectableStates.includes('New York') ? ' selectable' : '') +
                (selectedState === 'New York' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['New York']?.text || null}
              data-tooltipheading={tooltipData?.['New York']?.heading || null}
              onClick={() =>
                selectableStates.includes('New York')
                  ? onStateClick('New York')
                  : null
              }
            />
            <path
              state='Pennsylvania'
              id='PA'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M825.1,224.7l1.3-0.3l2.3-1.3l1.2-2.5l1.6-2.3l3.2-3.1v-0.8 l-2.4-1.6l-3.6-2.4l-1-2.6l-2.7-0.3l-0.2-1.1l-0.8-2.7l2.3-1.1l0.2-2.4l-1.3-1.3l0.2-1.6l1.9-3.1v-3.1l2.7-2.6l-0.9-0.7l-2.5-0.2 l-2.3-1.9l-1.5-6.1l-3.5,0.1l-2.5-2.7l-18.1,4.2l-43,8.7l-8.9,1.5l-0.6-6.5l-5.4,5.1l-1.3,0.5l-4.2,3l2.9,19.1l2.5,9.7l3.6,19.3 l3.3-0.6l11.9-1.5l37.9-7.7l14.9-2.8l8.3-1.6l0.3-0.2l2.1-1.6L825.1,224.7L825.1,224.7z'
              className={
                'map-state' +
                (selectableStates.includes('Pennsylvania')
                  ? ' selectable'
                  : '') +
                (selectedState === 'Pennsylvania' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Pennsylvania']?.text || null}
              data-tooltipheading={
                tooltipData?.['Pennsylvania']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('Pennsylvania')
                  ? onStateClick('Pennsylvania')
                  : null
              }
            />
            <path
              state='New Jersey'
              id='NJ'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M829.7,188.5l-2.3,2.7v3.1l-1.9,3.1l-0.2,1.6l1.3,1.3 l-0.2,2.4l-2.3,1.1l0.8,2.7l0.2,1.1l2.7,0.3l1,2.6l3.6,2.4l2.4,1.6v0.8l-3,2.7l-1.6,2.3l-1.5,2.7l-2.3,1.3L826,226l-0.2,1.2 l-0.6,2.6l1.1,2.2l3.2,2.9l4.8,2.3l4,0.6l0.2,1.5l-0.8,1l0.3,2.7h0.8l2.1-2.4l0.8-4.8l2.7-4l3.1-6.5l1.1-5.5l-0.6-1.1l-0.2-9.4 l-1.6-3.4l-1.1,0.8l-2.7,0.3l-0.5-0.5l1.1-1l2.1-1.9l0.1-1.1l-0.4-3.4l0.6-2.7l-0.1-2l-2.8-1.8l-5.1-1.2l-4.1-1.4L829.7,188.5 L829.7,188.5z'
              className={
                'map-state' +
                (selectableStates.includes('New Jersey') ? ' selectable' : '') +
                (selectedState === 'New Jersey' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['New Jersey']?.text || null}
              data-tooltipheading={tooltipData?.['New Jersey']?.heading || null}
              onClick={() =>
                selectableStates.includes('New Jersey')
                  ? onStateClick('New Jersey')
                  : null
              }
            />
            <path
              state='Delaware'
              id='DE'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M825.6,228.3l0.4-2.1l0.4-1.7l-1.6,0.4l-1.6,0.5l-2.2,1.8 l1.7,5l2.3,5.7l2.1,9.7l1.6,6.3l5-0.2l6.1-1.2l-2.3-7.4l-1,0.5l-3.6-2.4l-1.8-4.7l-1.9-3.6l-3.1-2.9l-0.9-2.1L825.6,228.3 L825.6,228.3z'
              className={
                'map-state' +
                (selectableStates.includes('Delaware') ? ' selectable' : '') +
                (selectedState === 'Delaware' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Delaware']?.text || null}
              data-tooltipheading={tooltipData?.['Delaware']?.heading || null}
              onClick={() =>
                selectableStates.includes('Delaware')
                  ? onStateClick('Delaware')
                  : null
              }
            />
            <path
              state='Maryland'
              id='MD'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M839.8,252.4l-6,1.2l-5.1,0.1l-1.8-6.9l-1.9-9.2l-2.6-6.2 l-1.3-4.4l-7.5,1.6l-14.9,2.8l-37.5,7.6l1.1,5l1,5.7l0.3-0.3l2.1-2.4l2.3-2.6l2.4-0.6l1.5-1.5l1.8-2.6l1.3,0.6l2.9-0.3l2.6-2.1 l2-1.5l1.8-0.5l1.6,1.1l2.9,1.5l1.9,1.8l1.2,1.5l4.1,1.7v2.9l5.5,1.3l1.1,0.5l1.4-2l2.9,2l-1.3,2.5l-0.8,4l-1.8,2.6v2.1l0.6,1.8 l5.1,1.4l4.3-0.1l3.1,1l2.1,0.3l1-2.1l-1.5-2.1v-1.8l-2.4-2.1l-2.1-5.5l1.3-5.3l-0.2-2.1l-1.3-1.3c0,0,1.5-1.6,1.5-2.3 s0.5-2.1,0.5-2.1l1.9-1.3l1.9-1.6l0.5,1l-1.5,1.6l-1.3,3.7l0.3,1.1l1.8,0.3l0.5,5.5l-2.1,1l0.3,3.6l0.5-0.2l1.1-1.9l1.6,1.8 l-1.6,1.3l-0.3,3.4l2.6,3.4l3.9,0.5l1.6-0.8l3.2,4.2l1.4,0.5l6.7-2.8l2-4L839.8,252.4L839.8,252.4z M823.8,261.4l1.1,2.5l0.2,1.8 l1.1,1.9c0,0,0.9-0.9,0.9-1.2s-0.7-3.1-0.7-3.1l-0.7-2.3L823.8,261.4L823.8,261.4z'
              className={
                'map-state' +
                (selectableStates.includes('Maryland') ? ' selectable' : '') +
                (selectedState === 'Maryland' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Maryland']?.text || null}
              data-tooltipheading={tooltipData?.['Maryland']?.heading || null}
              onClick={() =>
                selectableStates.includes('Maryland')
                  ? onStateClick('Maryland')
                  : null
              }
            />
            <path
              state='Virginia'
              id='VA'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M831.6,266.1l-0.1-1.9l6.5-2.5l-0.8,3.2l-2.9,3.8l-0.4,4.6 l0.5,3.4l-1.8,5l-2.2,1.9l-1.5-4.6l0.4-5.4l1.6-4.2L831.6,266.1L831.6,266.1z M835,294.4l-58.2,12.6l-37.4,5.3l-6.7-0.4l-2.6,1.9 l-7.3,0.2l-8.4,1l-10.9,1.6l10.5-5.6l0-2.1l1.5-2.1l10.6-11.5l3.9,4.5l3.8,1l2.5-1.1l2.2-1.3l2.5,1.3l3.9-1.4l1.9-4.6l2.6,0.5 l2.9-2.1l1.8,0.5l2.8-3.7l0.3-2.1l-1-1.3l1-1.9l5.3-12.3l0.6-5.7l1.2-0.5l2.2,2.4l3.9-0.3l1.9-7.6l2.8-0.6l1-2.7l2.6-2.3l2.8-5.7 l0.1-5.1l9.8,3.8c0.7,0.3,0.8-5,0.8-5l3.7,1.6l0.1,2.9l5.8,1.3l2.1,1.2l1.7,2.1l-0.7,3.6l-1.9,2.6l0.1,2.1l0.6,1.9l5,1.3l4.5,0 l3.1,1l1.9,0.3l0.7,3.1l3.2,0.4l0.9,1.2l-0.4,4.7l1.4,1.1l-0.5,1.9l1.2,0.8l-0.2,1.4l-2.7-0.1l0.1,1.6l2.3,1.5l0.1,1.4l1.8,1.8 l0.5,2.5l-2.6,1.4l1.6,1.5l5.8-1.7L835,294.4L835,294.4z'
              className={
                'map-state' +
                (selectableStates.includes('Virginia') ? ' selectable' : '') +
                (selectedState === 'Virginia' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Virginia']?.text || null}
              data-tooltipheading={tooltipData?.['Virginia']?.heading || null}
              onClick={() =>
                selectableStates.includes('Virginia')
                  ? onStateClick('Virginia')
                  : null
              }
            />
            <path
              state='West Virginia'
              id='WV'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M761.2,239l1.1,4.9l1.1,6l2.1-2.6l2.3-3.1l2.5-0.6l1.5-1.5 l1.8-2.6l1.4,0.6l2.9-0.3l2.6-2.1l2-1.5l1.8-0.5l1.3,1l3.6,1.8l1.9,1.8l1.4,1.3l-0.8,5.6l-5.8-2.5l-4.2-1.6l-0.1,5.2l-2.7,5.5 l-2.5,2.4l-1.2,2.7l-2.6,0.5l-0.9,3.6l-1,3.9l-4,0.3l-2.3-2.4l-1.1,0.6l-0.6,5.5l-1.3,3.5l-5,11l0.9,1.2l-0.2,1.9l-2.8,3.9l-1.8-0.5 l-3,2.2l-2.5-0.6l-2,4.6c0,0-3.3,1.4-3.9,1.4c-0.2,0-2.5-1.2-2.5-1.2l-2.3,1.4l-2.4,1l-3.7-0.9l-1.1-1.2l-2.2-3l-3.1-2l-1.7-3.6 l-4.3-3.5l-0.6-2.3l-2.6-1.5l-0.8-1.6l-0.2-5.3l2.2-0.1l1.9-0.8l0.2-2.7l1.6-1.5l0.2-5l1-3.9l1.3-0.6l1.3,1.1l0.5,1.8l1.8-1l0.5-1.6 l-1.1-1.8v-2.4l1-1.3l2.3-3.4l1.3-1.5l2.1,0.5l2.3-1.6l3.1-3.4l2.3-3.9l0.3-5.7l0.5-5v-4.7l-1.1-3.1l1-1.5l1.3-1.3l3.5,19.8l4.6-0.8 L761.2,239L761.2,239z'
              className={
                'map-state' +
                (selectableStates.includes('West Virginia')
                  ? ' selectable'
                  : '') +
                (selectedState === 'West Virginia' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['West Virginia']?.text || null}
              data-tooltipheading={
                tooltipData?.['West Virginia']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('West Virginia')
                  ? onStateClick('West Virginia')
                  : null
              }
            />
            <path
              state='Ohio'
              id='OH'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M735.3,193.3l-6.1,4.1l-3.9,2.3l-3.4,3.7l-4,3.9l-3.2,0.8 l-2.9,0.5l-5.5,2.6l-2.1,0.2l-3.4-3.1l-5.2,0.6l-2.6-1.5l-2.4-1.4l-4.9,0.7l-10.2,1.6l-11.2,2.2l1.3,14.6l1.8,13.7l2.6,23.4l0.6,4.8 l4.1-0.1l2.4-0.8l3.4,1.5l2.1,4.4l5.1,0l1.9,2.1l1.8-0.1l2.5-1.3l2.5,0.4l5.4,0.5l1.7-2.1l2.3-1.3l2.1-0.7l0.6,2.7l1.8,1l3.5,2.3 l2.2-0.1l1.3-0.5l0.2-2.8l1.6-1.5l0.1-4.8l1-4.1l1.3-0.6l1.3,1.1l0.5,1.7l1.7-1l0.4-1.5l-1.1-1.9l0.1-2.3l0.7-1.1l2.2-3.3l1-1.5 l2.1,0.5l2.3-1.6l3.1-3.4l2.8-4.1l0.3-5.1l0.5-5l-0.2-5.3l-1-2.9l0.4-1.2l1.8-1.8l-2.3-9L735.3,193.3L735.3,193.3z'
              className={
                'map-state' +
                (selectableStates.includes('Ohio') ? ' selectable' : '') +
                (selectedState === 'Ohio' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Ohio']?.text || null}
              data-tooltipheading={tooltipData?.['Ohio']?.heading || null}
              onClick={() =>
                selectableStates.includes('Ohio') ? onStateClick('Ohio') : null
              }
            />
            <path
              state='Indiana'
              id='IN'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M619.6,300l0.1-2.9l0.5-4.5l2.3-2.9l1.8-3.9l2.6-4.2l-0.5-5.8 l-1.8-2.7l-0.3-3.2l0.8-5.5l-0.5-7l-1.3-16l-1.3-15.4l-1-11.7l3.1,0.9l1.5,1l1.1-0.3l2.1-1.9l2.8-1.6l5.1-0.2l22-2.3l5.6-0.5l1.5,16 l4.3,36.8l0.6,5.8l-0.4,2.3l1.2,1.8l0.1,1.4l-2.5,1.6l-3.5,1.6l-3.2,0.6l-0.6,4.9l-4.6,3.3l-2.8,4l0.3,2.4l-0.6,1.5h-3.3l-1.6-1.6 l-2.5,1.3l-2.7,1.5l0.2,3.1l-1.2,0.3l-0.5-1l-2.2-1.5l-3.2,1.3l-1.6,3l-1.4-0.8l-1.5-1.6l-4.5,0.5l-5.6,1L619.6,300L619.6,300z'
              className={
                'map-state' +
                (selectableStates.includes('Indiana') ? ' selectable' : '') +
                (selectedState === 'Indiana' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Indiana']?.text || null}
              data-tooltipheading={tooltipData?.['Indiana']?.heading || null}
              onClick={() =>
                selectableStates.includes('Indiana')
                  ? onStateClick('Indiana')
                  : null
              }
            />
            <path
              state='Illinois'
              id='IL'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M619.5,300.3l0-3.2l0.6-4.6l2.3-2.9l1.9-4.1l2.2-4l-0.4-5.3 l-2-3.5l-0.1-3.3l0.7-5.3l-0.8-7.2l-1.1-15.8l-1.3-15l-0.9-11.6l-0.3-0.9l-0.8-2.6l-1.3-3.7l-1.6-1.8l-1.5-2.6l-0.2-5.5l-45.8,2.6 l0.2,2.4l2.3,0.7l0.9,1.1l0.5,1.8l3.9,3.4l0.7,2.3l-0.7,3.4l-1.8,3.7l-0.7,2.5l-2.3,1.8l-1.8,0.7l-5.3,1.4l-0.7,1.8l-0.7,2.1 l0.7,1.4l1.8,1.6l-0.2,4.1l-1.8,1.6l-0.7,1.6v2.7l-1.8,0.5l-1.6,1.1l-0.2,1.4l0.2,2.1l-1.7,1.3l-1,2.8l0.5,3.7l2.3,7.3l7.3,7.5 l5.5,3.7l-0.2,4.3l0.9,1.4l6.4,0.5l2.7,1.4l-0.7,3.7l-2.3,5.9l-0.7,3.2l2.3,3.9l6.4,5.3l4.6,0.7l2.1,5l2.1,3.2l-0.9,3l1.6,4.1 l1.8,2.1l1.4-0.9l0.9-2.1l2.2-1.7l2.1-0.6l2.6,1.2l3.6,1.4l1.2-0.3l0.2-2.3l-1.3-2.4l0.3-2.4l1.8-1.3l3-0.8l1.3-0.5l-0.6-1.4 l-0.8-2.4l1.4-1L619.5,300.3L619.5,300.3z'
              className={
                'map-state' +
                (selectableStates.includes('Illinois') ? ' selectable' : '') +
                (selectedState === 'Illinois' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Illinois']?.text || null}
              data-tooltipheading={tooltipData?.['Illinois']?.heading || null}
              onClick={() =>
                selectableStates.includes('Illinois')
                  ? onStateClick('Illinois')
                  : null
              }
            />
            <path
              state='Connecticut'
              id='CT'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M874.1,178.9l-3.7-14.9l-4.7,0.9l-21.2,4.7l1,3.2l1.5,7.3 l0.2,9l-1.2,2.2l1.9,1.9l4.3-3.9l3.6-3.2l1.9-2.1l0.8,0.6l2.7-1.5l5.2-1.1L874.1,178.9L874.1,178.9z'
              className={
                'map-state' +
                (selectableStates.includes('Connecticut')
                  ? ' selectable'
                  : '') +
                (selectedState === 'Connecticut' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Connecticut']?.text || null}
              data-tooltipheading={
                tooltipData?.['Connecticut']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('Connecticut')
                  ? onStateClick('Connecticut')
                  : null
              }
            />
            <path
              state='Wisconsin'
              id='WI'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M615.1,197.4l-0.1-3.2l-1.2-4.5l-0.6-6.1l-1.1-2.4l1-3.1 l0.8-2.9l1.5-2.6l-0.6-3.4l-0.6-3.6l0.5-1.8l1.9-2.4l0.2-2.7l-0.8-1.3l0.6-2.6l-0.5-4.2l2.7-5.7l2.9-6.8l0.2-2.3l-0.3-1l-0.8,0.5 l-4.2,6.3l-2.7,4l-1.9,1.8l-0.8,2.3l-2,0.8l-1.1,1.9l-1.5-0.3l-0.2-1.8l1.3-2.4l2.1-4.7l1.8-1.6l1-2.4l-2.6-1.9l-2-10.4l-3.5-1.3 l-1.9-2.3l-12.1-2.7l-2.9-1l-8.2-2.2l-7.9-1.2l-3.8-5.1l-0.8,0.6l-1.2-0.2l-0.6-1.1l-1.3,0.3l-1.1,0.2l-1.8,1l-1-0.6l0.6-1.9 l1.9-3.1l1.1-1.1l-1.9-1.5l-2.1,0.8l-2.9,1.9l-7.4,3.2l-2.9,0.6l-2.9-0.5l-1-0.9l-2.1,2.8l-0.2,2.7v8.5l-1.1,1.6l-5.3,3.9l-2.3,5.9 l0.5,0.2l2.5,2.1l0.7,3.2l-1.8,3.2v3.9l0.5,6.6l3,3h3.4l1.8,3.2l3.4,0.5l3.9,5.7l7.1,4.1l2.1,2.7l0.9,7.4l0.7,3.3l2.3,1.6l0.2,1.4 l-2.1,3.4l0.2,3.2l2.5,3.9l2.5,1.1l3,0.5l1.3,1.4L615.1,197.4L615.1,197.4z'
              className={
                'map-state' +
                (selectableStates.includes('Wisconsin') ? ' selectable' : '') +
                (selectedState === 'Wisconsin' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Wisconsin']?.text || null}
              data-tooltipheading={tooltipData?.['Wisconsin']?.heading || null}
              onClick={() =>
                selectableStates.includes('Wisconsin')
                  ? onStateClick('Wisconsin')
                  : null
              }
            />
            <path
              state='North Carolina'
              id='NC'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M835,294.3l2.1,4.9l3.6,6.5l2.4,2.4l0.6,2.3l-2.4,0.2l0.8,0.6 l-0.3,4.2l-2.6,1.3l-0.6,2.1l-1.3,2.9l-3.7,1.6l-2.4-0.3l-1.5-0.2l-1.6-1.3l0.3,1.3v1h1.9l0.8,1.3l-1.9,6.3h4.2l0.6,1.6l2.3-2.3 l1.3-0.5l-1.9,3.6l-3.1,4.8h-1.3l-1.1-0.5l-2.7,0.6l-5.2,2.4l-6.5,5.3l-3.4,4.7l-1.9,6.5l-0.5,2.4l-4.7,0.5l-5.5,1.3l-9.9-8.2 l-12.6-7.6l-2.9-0.8l-12.6,1.5l-4.3,0.8l-1.6-3.2l-3-2.1l-16.5,0.5l-7.3,0.8l-9.1,4.5l-6.1,2.6l-21.2,2.6l0.5-4.1l1.8-1.5l2.7-0.6 l0.6-3.7l4.2-2.7l3.9-1.5l4.2-3.6l4.4-2.1l0.6-3.1l3.9-3.9l0.6-0.2c0,0,0,1.1,0.8,1.1c0.8,0,1.9,0.3,1.9,0.3l2.3-3.6l2.1-0.6 l2.3,0.3l1.6-3.6l2.9-2.6l0.5-2.1l0.2-3.6l4.3,0l7.2-0.9l15.8-2.3l15.1-2.1l21.6-4.7l20-4.3l11.2-2.4L835,294.3L835,294.3z M839.3,327.5l2.6-2.5l3.2-2.6l1.5-0.6l0.2-2l-0.6-6.1l-1.5-2.3l-0.6-1.9l0.7-0.2l2.7,5.5l0.4,4.4l-0.2,3.4l-3.4,1.5l-2.8,2.4 l-1.1,1.2L839.3,327.5L839.3,327.5z'
              className={
                'map-state' +
                (selectableStates.includes('North Carolina')
                  ? ' selectable'
                  : '') +
                (selectedState === 'North Carolina' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['North Carolina']?.text || null}
              data-tooltipheading={
                tooltipData?.['North Carolina']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('North Carolina')
                  ? onStateClick('North Carolina')
                  : null
              }
            />
            <path
              state='District Of Columbia'
              id='DC'
              fill='#EAEAEA'
              stroke='#000000'
              strokeWidth='0.75'
              d='M805.8,250.8L804,249l-1.2-0.7l1.4-2l2.9,1.9L805.8,250.8 L805.8,250.8z'
              className={
                'map-state' +
                (selectableStates.includes('District Of Columbia')
                  ? ' selectable'
                  : '') +
                (selectedState === 'District Of Columbia' ? ' selected' : '')
              }
              data-tooltiptext={
                tooltipData?.['District Of Columbia']?.text || null
              }
              data-tooltipheading={
                tooltipData?.['District Of Columbia']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('District Of Columbia')
                  ? onStateClick('District Of Columbia')
                  : null
              }
            />
            <path
              state='Massachusetts'
              id='MA'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M899.6,173.3l2.2-0.7l0.5-1.7l1,0.1l1,2.3l-1.3,0.5l-3.9,0.1 L899.6,173.3z M890.2,174.1l2.3-2.6h1.6l1.8,1.5l-2.4,1l-2.2,1L890.2,174.1L890.2,174.1z M855.5,152.1l17.6-4.6l2.3-0.6l1.9-2.8 l3.7-1.7l2.9,4.4l-2.4,5.2l-0.3,1.5l1.9,2.6l1.1-0.8h1.8l2.3,2.6l3.9,6l3.6,0.5l2.3-1l1.8-1.8l-0.8-2.7l-2.1-1.6l-1.5,0.8l-1-1.3 l0.5-0.5l2.1-0.2l1.8,0.8l1.9,2.4l1,2.9l0.3,2.4l-4.2,1.5l-3.9,1.9l-3.9,4.5l-1.9,1.5v-1l2.4-1.5l0.5-1.8l-0.8-3.1l-2.9,1.5 l-0.8,1.5l0.5,2.3l-2.1,1l-2.7-4.5l-3.4-4.4l-2.1-1.8l-6.5,1.9l-5.1,1.1l-20.7,4.6l-0.7-4.8l0.6-10.6l4.3-0.9L855.5,152.1 L855.5,152.1z'
              className={
                'map-state' +
                (selectableStates.includes('Massachusetts')
                  ? ' selectable'
                  : '') +
                (selectedState === 'Massachusetts' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Massachusetts']?.text || null}
              data-tooltipheading={
                tooltipData?.['Massachusetts']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('Massachusetts')
                  ? onStateClick('Massachusetts')
                  : null
              }
            />
            <path
              state='Tennessee'
              id='TN'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M696.7,318.3l-51.9,5L629,325l-4.6,0.5l-3.9,0l-0.2,4.1 l-8.2,0.3l-7,0.6l-8.1-0.1l-1.4,7.1L594,343l-3.3,2.8l-1.3,4.4l-0.3,2.6l-4,2.3l1.5,3.6l-1,4.4l-1,0.8l108.2-10.4l0.4-4l1.8-1.5 l2.8-0.7l0.7-3.7l4.1-2.7l4-1.5l4.1-3.6l4.4-2l0.5-3.1l4.1-4l0.6-0.1c0,0,0,1.1,0.8,1.1s1.9,0.4,1.9,0.4l2.3-3.6l2.1-0.6l2.3,0.3 l1.6-3.5l3-2.6l0.4-1.9l0.3-3.7l-2.1-0.2l-2.6,2l-7,0l-18.4,2.4L696.7,318.3L696.7,318.3z'
              className={
                'map-state' +
                (selectableStates.includes('Tennessee') ? ' selectable' : '') +
                (selectedState === 'Tennessee' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Tennessee']?.text || null}
              data-tooltipheading={tooltipData?.['Tennessee']?.heading || null}
              onClick={() =>
                selectableStates.includes('Tennessee')
                  ? onStateClick('Tennessee')
                  : null
              }
            />
            <path
              state='Arkansas'
              id='AR'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M593.8,343.1l-4,0.7l-5.1-0.6l0.4-1.6l3-2.6l0.9-3.7l-1.8-3 l-78.4,2.5l1.6,6.9v8.2l1.4,11l0.2,37.8l2.3,1.9l3-1.4l2.7,1.1l0.7,6.6l55.6-1.1l1.1-2.1l-0.3-3.5l-1.8-3l1.6-1.5l-1.6-2.5l0.7-2.5 l1.4-5.6l2.5-2.1l-0.7-2.3l3.7-5.4l2.7-1.4l-0.1-1.5l-0.3-1.8l2.9-5.6l2.4-1.3l0.4-3.4l1.8-1.2l-3.1-0.5l-1.3-4l2.8-2.4l0.6-2l1.3-4 L593.8,343.1L593.8,343.1z'
              className={
                'map-state' +
                (selectableStates.includes('Arkansas') ? ' selectable' : '') +
                (selectedState === 'Arkansas' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Arkansas']?.text || null}
              data-tooltipheading={tooltipData?.['Arkansas']?.heading || null}
              onClick={() =>
                selectableStates.includes('Arkansas')
                  ? onStateClick('Arkansas')
                  : null
              }
            />
            <path
              state='Missouri'
              id='MO'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M558.4,248.1l-2.5-3.1l-1.1-2.3l-64.4,2.4l-2.3,0.1l1.3,2.5 l-0.2,2.3l2.5,3.9l3.1,4.1l3.1,2.7l2.2,0.2l1.5,0.9v3l-1.8,1.6l-0.5,2.3l2.1,3.4l2.5,3l2.5,1.8l1.4,11.7l0.3,36.1l0.2,4.7l0.5,5.4 l22.4-0.9l23.2-0.7l20.8-0.8l11.7-0.2l2.2,3.4l-0.7,3.3l-3.1,2.4l-0.6,1.8l5.4,0.5l3.9-0.7l1.7-5.5l0.7-5.9l2.1-2.6l2.6-1.5l0.1-3 l1-1.9l-1.7-2.5l-1.3,1l-2-2.2l-1.3-4.8l0.8-2.5l-1.9-3.4l-1.8-4.6l-4.8-0.8l-7-5.6l-1.7-4.1l0.8-3.2l2.1-6.1l0.5-2.9l-1.9-1 l-6.9-0.8l-1-1.7l-0.1-4.2l-5.5-3.4l-7-7.8l-2.3-7.3l-0.2-4.2L558.4,248.1L558.4,248.1z'
              className={
                'map-state' +
                (selectableStates.includes('Missouri') ? ' selectable' : '') +
                (selectedState === 'Missouri' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Missouri']?.text || null}
              data-tooltipheading={tooltipData?.['Missouri']?.heading || null}
              onClick={() =>
                selectableStates.includes('Missouri')
                  ? onStateClick('Missouri')
                  : null
              }
            />
            <path
              state='Georgia'
              id='Georgia'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M672.3,355.6v2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1 l1.6,4.8l1.5,7l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1l-1.8,4.8l-0.5,3.2l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8 l0.2,3.2l2.2,3.3l2.3,2.2l7.9,0.2L715,447l21.5-1.3l5.4-0.7l4.6,0l0.2,2.9l2.6,0.8l0.3-4.4l-1.6-4.5l1.1-1.6l5.8,0.8l5,0.3l-0.8-6.3 l2.3-10l1.5-4.2l-0.5-2.6l3.3-6.2l-0.5-1.4l-1.9,0.7l-2.6-1.3L760,406l-1.3-3.6l-2.3-2.1l-2.6-0.6l-1.6-4.9l-2.9-6.3l-4.2-1.9 l-2.1-1.9l-1.3-2.6l-2.1-1.9l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5l-3.4-5l-3.5,0.1l-3.8-2.4l-1.4-1.3 l-0.3-1.8l0.9-1.9l2.2-1.1l-0.6-2.1L672.3,355.6L672.3,355.6z'
              className={
                'map-state' +
                (selectableStates.includes('Georgia') ? ' selectable' : '') +
                (selectedState === 'Georgia' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Georgia']?.text || null}
              data-tooltipheading={tooltipData?.['Georgia']?.heading || null}
              onClick={() =>
                selectableStates.includes('Georgia')
                  ? onStateClick('Georgia')
                  : null
              }
            />
            <path
              state='South Carolina'
              id='SC'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M764.9,408.2l-1.8,1l-2.6-1.3l-0.6-2.1l-1.3-3.6l-2.3-2.1 l-2.6-0.6l-1.6-4.8l-2.7-6l-4.2-1.9l-2.1-1.9l-1.3-2.6l-2.1-1.9l-2.3-1.3l-2.3-2.9l-3.1-2.3l-4.5-1.8l-0.5-1.5l-2.4-2.9l-0.5-1.5 l-3.4-5.2l-3.4,0.2l-4-2.4l-1.3-1.3l-0.3-1.8l0.8-1.9l2.3-1l-0.5-2.3l5.8-2.3l9.1-4.6l7.8-0.8l16.1-0.4l2.6,1.9l1.7,3.4l4.3-0.6 l12.6-1.5l2.9,0.8l12.6,7.6l10.1,8.1l-5.4,5.5l-2.6,6.1l-0.5,6.3l-1.6,0.8l-1.1,2.7l-2.4,0.6l-2.1,3.6l-2.7,2.7l-2.3,3.4l-1.6,0.8 L774,398l-2.9,0.2l1,3.2l-5,5.5L764.9,408.2L764.9,408.2z'
              className={
                'map-state' +
                (selectableStates.includes('South Carolina')
                  ? ' selectable'
                  : '') +
                (selectedState === 'South Carolina' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['South Carolina']?.text || null}
              data-tooltipheading={
                tooltipData?.['South Carolina']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('South Carolina')
                  ? onStateClick('South Carolina')
                  : null
              }
            />
            <path
              state='Kentucky'
              id='KY'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M726,295.3l-2.3,2.4l-3.6,4l-4.9,5.5l-1.2,1.7l-0.1,2.1 l-4.4,2.2l-5.7,3.4l-7.2,1.8l-51.9,4.9L629,325l-4.6,0.5l-3.9,0l-0.2,4.2l-8.2,0.1l-7,0.6l-8-0.1l1.2-1.3l2.5-1.5l0.2-3.2l0.9-1.8 l-1.6-2.5l0.8-1.9l2.3-1.8l2.1-0.6l2.7,1.3l3.6,1.3l1.1-0.3l0.2-2.3l-1.3-2.4l0.3-2.3l1.9-1.5l2.6-0.6l1.6-0.6l-0.8-1.8l-0.6-1.9 l1.5-1c0,0,1.3-3.5,1.2-3.7l3.1-1.5l5.3-1l4.5-0.5l1.4,1.6l1.5,0.9l1.6-3.1l3.2-1.3l2.2,1.5l0.4,1l1.2-0.3l-0.2-3l3.1-1.7l2.1-1.1 l1.5,1.7l3.3,0l0.6-1.6l-0.4-2.3l2.6-4l4.8-3.4l0.7-4.8l2.9-0.5l3.8-1.6l2.4-1.7l-0.2-1.6l-1.1-1.5l0.6-3l4.2-0.1l2.3-0.7l3.3,1.4 l2.1,4.4l5.1,0l2.1,2.2l1.6-0.1l2.6-1.3l5.2,0.6l2.6,0.2l1.7-2.1l2.6-1.4l1.9-0.7l0.6,2.8l2,1.1l2.6,2.1l0.1,5.7l0.8,1.6l2.6,1.6 l0.8,2.3l4.2,3.4l1.8,3.6L726,295.3L726,295.3z'
              className={
                'map-state' +
                (selectableStates.includes('Kentucky') ? ' selectable' : '') +
                (selectedState === 'Kentucky' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Kentucky']?.text || null}
              data-tooltipheading={tooltipData?.['Kentucky']?.heading || null}
              onClick={() =>
                selectableStates.includes('Kentucky')
                  ? onStateClick('Kentucky')
                  : null
              }
            />
            <path
              state='Alabama'
              id='Alabama'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M631.3,460.4l-1.5-14.3l-2.7-18.8l0.2-14.1l0.8-31l-0.2-16.7 l0.2-6.4l44.5-3.6l-0.1,2.2l0.2,2.1l0.6,3.4l3.4,7.9l2.4,9.9l1.5,6.1l1.6,4.8l1.5,7l2.1,6.3l2.6,3.4l0.5,3.4l1.9,0.8l0.2,2.1 l-1.8,4.8l-0.5,3.2l-0.2,1.9l1.6,4.4l0.3,5.3l-0.8,2.4l0.6,0.8l1.5,0.8l0.3,2.9l-5.6-0.4l-6.8,0.6l-25.5,2.9l-10.4,1.4l-0.2,2.9 l1.8,1.8l2.6,1.9l0.6,7.9l-5.5,2.6L640,463l2.7-1.9v-1l-3.1-6l-2.3-0.6l-1.5,4.4l-1.3,2.7l-0.6-0.2H631.3L631.3,460.4z'
              className={
                'map-state' +
                (selectableStates.includes('Alabama') ? ' selectable' : '') +
                (selectedState === 'Alabama' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Alabama']?.text || null}
              data-tooltipheading={tooltipData?.['Alabama']?.heading || null}
              onClick={() =>
                selectableStates.includes('Alabama')
                  ? onStateClick('Alabama')
                  : null
              }
            />
            <path
              state='Louisiana'
              id='LA'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M608,459.2l-3.3-3.2l1-5.5l-0.7-0.9l-9.3,1l-25,0.5l-0.7-2.4 l0.9-8.5l3.3-5.9l5-8.7l-0.6-2.4l1.3-0.7l0.5-2l-2.3-2.1l-0.1-1.9l-1.8-4.3l-0.1-6.3l-55.5,0.9l0,9.6l0.7,9.4l0.7,3.9l2.5,4.1l0.9,5 l4.3,5.5l0.2,3.2l0.7,0.7l-0.7,8.5l-3,5l1.6,2.1l-0.7,2.5l-0.7,7.3l-1.4,3.2l0.1,3.6l4.7-1.5l12.1,0.2l10.3,3.6l6.5,1.1l3.7-1.5 l3.2,1.1l3.2,1l0.8-2.1l-3.2-1.1l-2.6,0.5l-2.7-1.6c0,0,0.2-1.3,0.8-1.5c0.6-0.2,3.1-1,3.1-1l1.8,1.5l1.8-1l3.2,0.6l1.5,2.4l0.3,2.3 l4.5,0.3l1.8,1.8l-0.8,1.6l-1.3,0.8l1.6,1.6l8.4,3.6l3.6-1.3l1-2.4l2.6-0.6l1.8-1.5l1.3,1l0.8,2.9l-2.3,0.8l0.6,0.6l3.4-1.3l2.3-3.4 l0.8-0.5l-2.1-0.3l0.8-1.6l-0.2-1.5l2.1-0.5l1.1-1.3l0.6,0.8c0,0-0.2,3.1,0.6,3.1s4.2,0.6,4.2,0.6l4,1.9l1,1.5h2.9l1.1,1l2.3-3.1 v-1.5h-1.3l-3.4-2.7l-5.8-0.8l-3.2-2.3l1.1-2.7l2.3,0.3l0.2-0.6l-1.8-1v-0.5h3.2l1.8-3.1l-1.3-1.9l-0.3-2.7l-1.5,0.2l-1.9,2.1 l-0.6,2.6l-3.1-0.6l-1-1.8l1.8-1.9l1.9-3.4l-1.1-2.4L608,459.2L608,459.2z'
              className={
                'map-state' +
                (selectableStates.includes('Louisiana') ? ' selectable' : '') +
                (selectedState === 'Louisiana' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Louisiana']?.text || null}
              data-tooltipheading={tooltipData?.['Louisiana']?.heading || null}
              onClick={() =>
                selectableStates.includes('Louisiana')
                  ? onStateClick('Louisiana')
                  : null
              }
            />
            <path
              state='Mississippi'
              id='MS'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M631.6,459.3l-0.3,1.3h-5.2l-1.5-0.8l-2.1-0.3l-6.8,1.9 l-1.8-0.8l-2.6,4.2l-1.1,0.8l-1.1-2.5l-1.1-3.9l-3.4-3.2l1.1-5.5l-0.7-0.9l-1.8,0.2l-7.9,0.9l-24.5,0.4l-0.8-2.2l0.9-8.4l3.1-5.7 l5.2-9.1l-0.4-2.4l1.2-0.7l0.4-1.9l-2.3-2.1l-0.1-2.1l-1.8-4.1l-0.1-6l1.3-2.5l-0.2-3.4l-1.8-3.1l1.5-1.5l-1.6-2.5l0.5-1.7l1.6-6.5 l2.5-2l-0.6-2.4l3.7-5.3l2.8-1.4l-0.2-1.7l-0.3-1.7l2.9-5.6l2.3-1.2l0.2-0.9l37.3-3.9l0.2,6.3l0.2,16.7l-0.8,31l-0.2,14.1l2.7,18.8 L631.6,459.3L631.6,459.3z'
              className={
                'map-state' +
                (selectableStates.includes('Mississippi')
                  ? ' selectable'
                  : '') +
                (selectedState === 'Mississippi' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Mississippi']?.text || null}
              data-tooltipheading={
                tooltipData?.['Mississippi']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('Mississippi')
                  ? onStateClick('Mississippi')
                  : null
              }
            />
            <path
              state='Iowa'
              id='IA'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M569.2,199.6l0.3,2.8l2.2,0.6l1,1.2l0.5,1.9l3.8,3.4l0.7,2.4 l-0.7,3.4l-1.6,3.2l-0.8,2.7l-2.2,1.6l-1.7,0.6l-5.6,1.9l-1.4,3.8l0.7,1.4l1.8,1.7l-0.3,4l-1.8,1.5l-0.8,1.6l0.1,2.8l-1.9,0.5 l-1.6,1.1l-0.3,1.4l0.3,2.1l-1.6,1.1l-2.5-3.1l-1.3-2.4l-65.7,2.5l-0.9,0.2l-2.1-4.5l-0.2-6.6l-1.6-4.1l-0.7-5.3l-2.3-3.7l-0.9-4.8 l-2.7-7.5l-1.1-5.4l-1.4-2.2l-1.6-2.7l2-4.8l1.4-5.7l-2.7-2.1l-0.5-2.7l0.9-2.5h1.7l82.7-1.3l0.8,4.2l2.3,1.6l0.3,1.4l-2,3.4 l0.2,3.2l2.5,3.8l2.5,1.3l3.1,0.5L569.2,199.6z'
              className={
                'map-state' +
                (selectableStates.includes('Iowa') ? ' selectable' : '') +
                (selectedState === 'Iowa' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Iowa']?.text || null}
              data-tooltipheading={tooltipData?.['Iowa']?.heading || null}
              onClick={() =>
                selectableStates.includes('Iowa') ? onStateClick('Iowa') : null
              }
            />
            <path
              state='Minnesota'
              id='MN'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M475.2,128.8l-0.5-8.5L473,113l-1.8-13.5l-0.5-9.8l-1.8-3.4 l-1.6-5V71l0.7-3.9l-1.8-5.5l30.1,0l0.3-8.2l0.6-0.2l2.3,0.5l1.9,0.8l0.8,5.5l1.5,6.1l1.6,1.6h4.8l0.3,1.5l6.3,0.3v2.1h4.8l0.3-1.3 l1.1-1.1l2.3-0.6l1.3,1h2.9l3.9,2.6l5.3,2.4l2.4,0.5l0.5-1l1.5-0.5l0.5,2.9l2.6,1.3l0.5-0.5l1.3,0.2v2.1l2.6,1h3.1l1.6-0.8l3.2-3.2 l2.6-0.5l0.8,1.8l0.5,1.3h1l1-0.8l8.9-0.3l1.8,3.1h0.6l0.7-1.1l4.4-0.4l-0.6,2.3l-3.9,1.8l-9.2,4.1l-4.8,2l-3.1,2.6l-2.4,3.6 l-2.3,3.9l-1.8,0.8l-4.5,5l-1.3,0.2l-4.3,2.8l-2.5,3.2l-0.2,3.2l0.1,8l-1.4,1.7l-5.1,3.8l-2.2,6l2.9,2.2l0.7,3.2l-1.9,3.2l0.2,3.7 l0.4,6.7l3,3h3.3l1.9,3.1l3.4,0.5l3.9,5.7l7.1,4.1l2.1,2.9l0.7,6.4l-81.2,1.1l-0.3-35.7l-0.5-3l-4.1-3.4l-1.1-1.8V135l2.1-1.6 l1.4-1.4L475.2,128.8L475.2,128.8z'
              className={
                'map-state' +
                (selectableStates.includes('Minnesota') ? ' selectable' : '') +
                (selectedState === 'Minnesota' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Minnesota']?.text || null}
              data-tooltipheading={tooltipData?.['Minnesota']?.heading || null}
              onClick={() =>
                selectableStates.includes('Minnesota')
                  ? onStateClick('Minnesota')
                  : null
              }
            />
            <path
              state='Oklahoma'
              id='OK'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M380.3,320.8l-16.7-1.3l-0.9,11l20.5,1.2l32.1,1.3l-2.3,24.4 l-0.5,17.8l0.2,1.6l4.3,3.7l2.1,1.1l0.7-0.2l0.7-2.1l1.4,1.8h2.1v-1.4l2.7,1.4l-0.5,3.9l4.1,0.2l2.5,1.1l4.1,0.7l2.5,1.8l2.3-2.1 l3.4,0.7l2.5,3.4h0.9v2.3l2.3,0.7l2.3-2.3l1.8,0.7h2.5l0.9,2.5l6.3,2.1l1.4-0.7l1.8-4.1h1.1l1.1,2.1l4.1,0.7l3.7,1.4l3,0.9l1.8-0.9 l0.7-2.5h4.3l2.1,0.9l2.7-2.1h1.1l0.7,1.6h4.1l1.6-2.1l1.8,0.5l2.1,2.5l3.2,1.8l3.2,0.9l1.9,1.1l-0.4-37.2l-1.4-11l-0.2-8.9 l-1.4-6.5l-0.8-7.2l-0.1-3.8l-12.1,0.3l-46.4-0.5l-45-2.1L380.3,320.8L380.3,320.8z'
              className={
                'map-state' +
                (selectableStates.includes('Oklahoma') ? ' selectable' : '') +
                (selectedState === 'Oklahoma' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Oklahoma']?.text || null}
              data-tooltipheading={tooltipData?.['Oklahoma']?.heading || null}
              onClick={() =>
                selectableStates.includes('Oklahoma')
                  ? onStateClick('Oklahoma')
                  : null
              }
            />
            <path
              state='Texas'
              id='Texas'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M361.5,330.6l22.7,1.1l31.1,1.1l-2.3,23.5l-0.3,18.2l0.1,2.1 l4.3,3.8l2,1.4l1.2-0.6l0.4-1.8l1.1,1.8l2.1,0l0-1.4l1.7,1l1.1,0.4l-0.4,4l4.1,0.1l2.9,1.2l4,0.5l2.4,2.1l2.1-2.1l3.7,0.6l2.2,3.2 l1.1,0.3l-0.2,2l2.2,0.8l2.3-2.1l2.1,0.6l2.2,0l0.9,2.4l6.3,2.1l1.6-0.8l1.5-4.2h0.3l0.9,0.1l1.2,2.1l3.9,0.7l3.3,1.1l3.4,1.2l1.8-1 l0.7-2.5l4.5,0l1.8,0.9l2.8-2.1l1.1,0l0.9,1.6h4.1l1.5-2l1.9,0.4l1.9,2.4l3.5,2l2.9,0.8l1.5,0.8l2.4,2l3-1.3l2.7,1.1l0.6,6.1l0,9.7 l0.7,9.5l0.7,3.6l2.7,4.4l0.9,5l4.2,5.5l0.2,3.1l0.7,0.8l-0.7,8.4l-2.9,5l1.5,2.2l-0.6,2.3l-0.7,7.4l-1.5,3.3l0.3,3.5l-5.7,1.6 l-9.9,4.5l-1,1.9l-2.6,1.9l-2.1,1.5l-1.3,0.8L498,498l-2.7,2.1l-5.3,3.2l-5.7,2.4l-6.3,3.4l-1.8,1.5l-5.8,3.6l-3.4,0.6l-3.9,5.5 l-4,0.3l-1,1.9l2.3,1.9l-1.5,5.5l-1.3,4.5l-1.1,3.9l-0.8,4.5l0.8,2.4l1.8,7l1,6.1l1.8,2.7l-1,1.5l-3.1,1.9l-5.7-3.9l-5.5-1.1 l-1.3,0.5l-3.2-0.6l-4.2-3.1l-5.2-1.1l-7.6-3.4l-2.1-3.9l-1.3-6.5l-3.2-1.9l-0.6-2.3l0.6-0.6l0.3-3.4l-1.3-0.6l-0.6-1l1.3-4.4 l-1.6-2.3l-3.2-1.3l-3.4-4.4l-3.6-6.6l-4.2-2.6l0.2-1.9l-5.3-12.3l-0.8-4.2l-1.8-1.9l-0.2-1.5l-6-5.3l-2.6-3.1v-1.1l-2.6-2.1 l-6.8-1.1l-7.4-0.6l-3.1-2.3l-4.5,1.8l-3.6,1.5l-2.3,3.2l-1,3.7l-4.4,6.1l-2.4,2.4l-2.6-1l-1.8-1.1l-1.9-0.6l-3.9-2.3v-0.6l-1.8-1.9 l-5.2-2.1l-7.4-7.8l-2.3-4.7v-8.1l-3.2-6.5l-0.5-2.7l-1.6-1l-1.1-2.1l-5-2.1l-1.3-1.6l-7.1-7.9l-1.3-3.2l-4.7-2.3l-1.5-4.4l-2.6-2.9 l-1.9-0.5l-0.6-4.7l8,0.7l29,2.7l29,1.6l2.2-19.5l3.9-55.6l1.6-18.7l1.4,0 M461.7,560.2l-0.6-7.1l-2.7-7.2l-0.6-7l1.5-8.2l3.3-6.9 l3.5-5.4l3.2-3.6l0.6,0.2l-4.8,6.6l-4.4,6.5l-2,6.6l-0.3,5.2l0.9,6.1l2.6,7.2l0.5,5.2l0.2,1.5L461.7,560.2L461.7,560.2z'
              className={
                'map-state' +
                (selectableStates.includes('Texas') ? ' selectable' : '') +
                (selectedState === 'Texas' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Texas']?.text || null}
              data-tooltipheading={tooltipData?.['Texas']?.heading || null}
              onClick={() =>
                selectableStates.includes('Texas')
                  ? onStateClick('Texas')
                  : null
              }
            />
            <path
              state='New Mexico'
              id='NM'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M288.2,424l-0.8-4.7l8.6,0.5l30.2,2.9l27.3,1.7l2.2-18.7 l3.9-55.9l1.7-19.4l1.6,0.1l0.8-11.2l-104-10.6l-17.5,120.4l15.5,2l1.3-10L288.2,424L288.2,424z'
              className={
                'map-state' +
                (selectableStates.includes('New Mexico') ? ' selectable' : '') +
                (selectedState === 'New Mexico' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['New Mexico']?.text || null}
              data-tooltipheading={tooltipData?.['New Mexico']?.heading || null}
              onClick={() =>
                selectableStates.includes('New Mexico')
                  ? onStateClick('New Mexico')
                  : null
              }
            />
            <path
              state='Kansas'
              id='KS'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M507.9,324.4l-12.6,0.2l-46.1-0.5l-44.6-2.1l-24.6-1.3 l3.9-64.6l22.1,0.7l40.3,0.8l44.3,1h5.1l2.2,2.2l2,0l1.6,1l-0.1,3l-1.8,1.7l-0.3,2.2l1.8,3.4l3,3.2l2.3,1.6l1.3,11.2L507.9,324.4 L507.9,324.4z'
              className={
                'map-state' +
                (selectableStates.includes('Kansas') ? ' selectable' : '') +
                (selectedState === 'Kansas' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Kansas']?.text || null}
              data-tooltipheading={tooltipData?.['Kansas']?.heading || null}
              onClick={() =>
                selectableStates.includes('Kansas')
                  ? onStateClick('Kansas')
                  : null
              }
            />
            <path
              state='Nebraska'
              id='NE'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M486.1,240.7l3.2,7l-0.1,2.3l3.5,5.5l2.7,3.2h-5l-43.5-0.9 l-40.8-0.9l-22.3-0.8l1.1-21.3l-32.3-2.9l4.3-44l15.5,1l20.1,1.1l17.8,1.1l23.8,1.1l10.7-0.5l2.1,2.3l4.8,3l1.1,0.9l4.3-1.4l3.9-0.5 l2.7-0.2l1.8,1.4l4.1,1.6l3,1.6l0.5,1.6l0.9,2.1h1.8l0.8,0l0.9,4.7l2.9,8.5l0.6,3.8l2.5,3.8l0.6,5.1l1.6,4.2L486.1,240.7 L486.1,240.7z'
              className={
                'map-state' +
                (selectableStates.includes('Nebraska') ? ' selectable' : '') +
                (selectedState === 'Nebraska' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Nebraska']?.text || null}
              data-tooltipheading={tooltipData?.['Nebraska']?.heading || null}
              onClick={() =>
                selectableStates.includes('Nebraska')
                  ? onStateClick('Nebraska')
                  : null
              }
            />
            <path
              state='South Dakota'
              id='SD'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M476.4,204l0-0.6l-2.9-4.8l1.9-4.7l1.5-5.9l-2.8-2.1l-0.4-2.7 l0.8-2.6l3.2,0l-0.1-5l-0.3-30.2l-0.6-3.8l-4.1-3.3l-1-1.7l-0.1-1.6l2-1.5l1.5-1.7l0.2-2.7l-58.3-1.6l-54.8-3.4l-5.3,63.7l14.6,0.9 l19.9,1.2l17.7,0.9l23.8,1.3l12-0.4l2,2.2l5.2,3.3l0.8,0.7l4.5-1.5l6.5-0.6l1.7,1.3l4.2,1.6l2.9,1.6l0.4,1.5l1,2.2L476.4,204 L476.4,204z'
              className={
                'map-state' +
                (selectableStates.includes('South Dakota')
                  ? ' selectable'
                  : '') +
                (selectedState === 'South Dakota' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['South Dakota']?.text || null}
              data-tooltipheading={
                tooltipData?.['South Dakota']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('South Dakota')
                  ? onStateClick('South Dakota')
                  : null
              }
            />
            <path
              state='North Dakota'
              id='ND'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M475.3,128.9l-0.6-8.4l-1.7-6.8l-1.9-13l-0.5-11l-1.7-3.1 l-1.8-5.2l0-10.4l0.6-3.8l-1.8-5.5l-28.6-0.6l-18.6-0.6l-26.5-1.3L369.3,57l-7,67.2l54.9,3.3L475.3,128.9L475.3,128.9z'
              className={
                'map-state' +
                (selectableStates.includes('North Dakota')
                  ? ' selectable'
                  : '') +
                (selectedState === 'North Dakota' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['North Dakota']?.text || null}
              data-tooltipheading={
                tooltipData?.['North Dakota']?.heading || null
              }
              onClick={() =>
                selectableStates.includes('North Dakota')
                  ? onStateClick('North Dakota')
                  : null
              }
            />
            <path
              state='Wyoming'
              id='WY'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M360.4,143.3l-106.7-13.5l-14.1,88.5l113.3,13.6L360.4,143.3z '
              className={
                'map-state' +
                (selectableStates.includes('Wyoming') ? ' selectable' : '') +
                (selectedState === 'Wyoming' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Wyoming']?.text || null}
              data-tooltipheading={tooltipData?.['Wyoming']?.heading || null}
              onClick={() =>
                selectableStates.includes('Wyoming')
                  ? onStateClick('Wyoming')
                  : null
              }
            />
            <path
              state='Montana'
              id='MT'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M369.2,57l-30.7-2.8l-29.3-3.6l-29.3-4l-32.3-5.3l-18.4-3.4 l-32.7-6.9L192,52.2l3.4,7.5l-1.4,4.6l1.8,4.6l3.2,1.4l4.6,10.8l2.7,3.2l0.5,1.1l3.4,1.1l0.5,2.1l-7.1,17.6v2.5l2.5,3.2h0.9l4.8-3 l0.7-1.1l1.6,0.7l-0.2,5.3l2.7,12.6l3,2.5l0.9,0.7l1.8,2.3l-0.5,3.4l0.7,3.4l1.1,0.9l2.3-2.3h2.7l3.2,1.6l2.5-0.9h4.1l3.7,1.6 l2.7-0.5l0.5-3l3-0.7l1.4,1.4l0.5,3.2l1.4,0.8l1.9-11l106.7,13.4L369.2,57L369.2,57z'
              className={
                'map-state' +
                (selectableStates.includes('Montana') ? ' selectable' : '') +
                (selectedState === 'Montana' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Montana']?.text || null}
              data-tooltipheading={tooltipData?.['Montana']?.heading || null}
              onClick={() =>
                selectableStates.includes('Montana')
                  ? onStateClick('Montana')
                  : null
              }
            />
            <path
              state='Colorado'
              id='Colorado'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M380,321l4.9-86.3L271.5,222l-12.2,87.9L380,321z'
              className={
                'map-state' +
                (selectableStates.includes('Colorado') ? ' selectable' : '') +
                (selectedState === 'Colorado' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Colorado']?.text || null}
              data-tooltipheading={tooltipData?.['Colorado']?.heading || null}
              onClick={() =>
                selectableStates.includes('Colorado')
                  ? onStateClick('Colorado')
                  : null
              }
            />
            <path
              state='Idaho'
              id='ID'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M148.5,176.5l8.8-35.2l1.4-4.2l2.5-5.9l-1.3-2.3l-2.5,0.1 l-0.8-1l0.5-1.1l0.3-3.1l4.5-5.5l1.8-0.5l1.1-1.1l0.6-3.2l0.9-0.7l3.9-5.8l3.9-4.3l0.2-3.8l-3.4-2.6l-1.5-4.4l13.6-63.3l13.5,2.5 l-4.4,21.4l3.6,7.5l-1.6,4.7l2,4.6l3.1,1.3l3.8,9.6l3.5,4.4l0.5,1.1l3.3,1.1l0.4,2.1l-7,17.4l-0.2,2.6l2.6,3.3l0.9,0l4.9-3l0.7-1.1 l1.6,0.7l-0.3,5.4l2.7,12.6l3.9,3.2l1.7,2.2l-0.7,4.1l1.1,2.8l1.1,1.1l2.5-2.4l2.8,0l2.9,1.3l2.8-0.7l3.8-0.2l4,1.6l2.7-0.3l0.5-3 l2.9-0.8l1.3,1.5l0.4,2.9l1.4,1.2l-8.4,53.6C243.4,194.7,155.5,178,148.5,176.5L148.5,176.5z'
              className={
                'map-state' +
                (selectableStates.includes('Idaho') ? ' selectable' : '') +
                (selectedState === 'Idaho' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Idaho']?.text || null}
              data-tooltipheading={tooltipData?.['Idaho']?.heading || null}
              onClick={() =>
                selectableStates.includes('Idaho')
                  ? onStateClick('Idaho')
                  : null
              }
            />
            <path
              state='Utah'
              id='UT'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M259.5,310.1l-83.7-11.9l20.6-112.5l46.8,8.7l-1.5,10.6 l-2.3,13.2l7.8,0.9l16.4,1.8l8.2,0.9L259.5,310.1z'
              className={
                'map-state' +
                (selectableStates.includes('Utah') ? ' selectable' : '') +
                (selectedState === 'Utah' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Utah']?.text || null}
              data-tooltipheading={tooltipData?.['Utah']?.heading || null}
              onClick={() =>
                selectableStates.includes('Utah') ? onStateClick('Utah') : null
              }
            />
            <path
              state='Arizona'
              id='AZ'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M144.9,382.6l-2.6,2.2l-0.3,1.5l0.5,1l18.9,10.7l12.1,7.6 l14.7,8.6l16.8,10l12.3,2.4l25,2.7l17.3-119.1l-83.7-11.9l-3.1,16.4l-1.6,0l-1.7,2.6l-2.5-0.1l-1.3-2.7l-2.7-0.3l-0.9-1.1H161 l-0.9,0.6l-1.9,1l-0.1,7l-0.2,1.7l-0.6,12.6l-1.5,2.2l-0.6,3.3l2.7,4.9l1.3,5.8l0.8,1l1,0.6l-0.1,2.3l-1.6,1.4l-3.4,1.7l-1.9,1.9 l-1.5,3.7l-0.6,4.9l-2.9,2.7l-2.1,0.7l0.1,0.8l-0.5,1.7l0.5,0.8l3.7,0.6l-0.6,2.7l-1.5,2.2L144.9,382.6L144.9,382.6z'
              className={
                'map-state' +
                (selectableStates.includes('Arizona') ? ' selectable' : '') +
                (selectedState === 'Arizona' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Arizona']?.text || null}
              data-tooltipheading={tooltipData?.['Arizona']?.heading || null}
              onClick={() =>
                selectableStates.includes('Arizona')
                  ? onStateClick('Arizona')
                  : null
              }
            />
            <path
              state='Nevada'
              id='Nevada'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M196.4,185.6l-23.6,128.8l-1.8,0.3l-1.6,2.4l-2.4,0l-1.5-2.7 l-2.6-0.4l-0.8-1.1l-1-0.1l-2.8,1.6l-0.3,6.8l-0.4,5.8l-0.3,8.6l-1.4,2.1l-2.4-1.1L84.3,232.5l19-67.6L196.4,185.6L196.4,185.6z'
              className={
                'map-state' +
                (selectableStates.includes('Nevada') ? ' selectable' : '') +
                (selectedState === 'Nevada' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Nevada']?.text || null}
              data-tooltipheading={tooltipData?.['Nevada']?.heading || null}
              onClick={() =>
                selectableStates.includes('Nevada')
                  ? onStateClick('Nevada')
                  : null
              }
            />
            <path
              state='Oregon'
              id='OR'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M148.7,175.5l8.9-34.8l1.1-4.2l2.4-5.6l-0.6-1.2l-2.5,0 l-1.3-1.7l0.5-1.5l0.5-3.2l4.5-5.5l1.8-1.1l1.1-1.1l1.5-3.6l4-5.7l3.6-3.9l0.2-3.5l-3.3-2.5l-1.8-4.6l-12.7-3.6l-15.1-3.5L126,84.9 l-0.5-1.4l-5.5,2.1l-4.5-0.6l-2.4-1.6l-1.3,0.7l-4.7-0.2l-1.7-1.4l-5.3-2.1l-0.8,0.1L95.2,79l-1.9,1.8l-6.2-0.3l-5.9-4.1l0.7-0.8 l0.2-7.8l-2.3-3.9l-4.1-0.6l-0.7-2.5l-2.4-0.5l-5.8,2.1l-2.3,6.5l-3.2,10l-3.2,6.5l-5,14.1L46.6,113l-8.1,12.6l-1.9,2.9l-0.8,8.6 l0.4,12.1L148.7,175.5L148.7,175.5z'
              className={
                'map-state' +
                (selectableStates.includes('Oregon') ? ' selectable' : '') +
                (selectedState === 'Oregon' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Oregon']?.text || null}
              data-tooltipheading={tooltipData?.['Oregon']?.heading || null}
              onClick={() =>
                selectableStates.includes('Oregon')
                  ? onStateClick('Oregon')
                  : null
              }
            />
            <path
              state='Washington'
              id='Washington'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M102.1,7.6l4.4,1.5l9.7,2.7l8.6,1.9l20,5.7l23,5.7l15.2,3.2 l-13.6,63.6l-12.4-3.5l-15.5-3.6l-15.2,0l-0.5-1.3l-5.6,2.2l-4.6-0.7l-2.1-1.6L112,84l-4.7-0.1l-1.7-1.3l-5.3-2.1l-0.7,0.1L95.2,79 l-1.9,1.8L87,80.5l-5.9-4.1l0.8-0.9l0.1-7.7l-2.3-3.8l-4.1-0.6l-0.7-2.5l-2.3-0.5l-3.6,1.2l-2.3-3.2l0.3-2.9l2.7-0.3l1.6-4L68.9,50 l0.2-3.7l4.4-0.6l-2.7-2.7l-1.5-7.1l0.6-2.9v-7.9l-1.8-3.2l2.3-9.4l2.1,0.5l2.4,2.9l2.7,2.6l3.2,1.9l4.5,2.1l3.1,0.6l2.9,1.5l3.4,1 l2.3-0.2v-2.4l1.3-1.1l2.1-1.3l0.3,1.1l0.3,1.8l-2.3,0.5l-0.3,2.1l1.8,1.5l1.1,2.4l0.6,1.9l1.5-0.2l0.2-1.3l-1-1.3l-0.5-3.2l0.8-1.8 l-0.6-1.5v-2.3l1.8-3.6l-1.1-2.6l-2.4-4.8l0.3-0.8L102.1,7.6L102.1,7.6z M92.6,13.6l2-0.2l0.5,1.4l1.5-1.6H99l0.8,1.5l-1.5,1.7 l0.6,0.8l-0.7,2l-1.4,0.4c0,0-0.9,0.1-0.9-0.2s1.5-2.6,1.5-2.6l-1.7-0.6l-0.3,1.5l-0.7,0.6l-1.5-2.3L92.6,13.6z'
              className={
                'map-state' +
                (selectableStates.includes('Washington') ? ' selectable' : '') +
                (selectedState === 'Washington' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['Washington']?.text || null}
              data-tooltipheading={tooltipData?.['Washington']?.heading || null}
              onClick={() =>
                selectableStates.includes('Washington')
                  ? onStateClick('Washington')
                  : null
              }
            />
            <path
              state='California'
              id='California'
              fill='#FFFFFF'
              stroke='#000000'
              strokeWidth='0.75'
              d='M144.7,382.2l3.9-0.5l1.5-2l0.5-2.9l-3.6-0.6l-0.5-0.7l0.5-2 l-0.2-0.6l1.9-0.6l3-2.8l0.6-5l1.4-3.4l1.9-2.2l3.5-1.6l1.7-1.6l0.1-2.1l-1-0.6l-1-1.1l-1.2-5.8l-2.7-4.8l0.6-3.5l-2.4-1L84.3,232.5 l18.9-67.6l-67.1-15.7l-1.5,4.7l-0.2,7.4l-5.2,11.8l-3.1,2.6l-0.3,1.1l-1.8,0.8l-1.5,4.2l-0.8,3.2l2.7,4.2l1.6,4.2l1.1,3.6l-0.3,6.5 l-1.8,3.1l-0.6,5.8l-1,3.7l1.8,3.9l2.7,4.5l2.3,4.8l1.3,4l-0.3,3.2l-0.3,0.5v2.1l5.7,6.3l-0.5,2.4l-0.6,2.3l-0.6,1.9l0.2,8.2 l2.1,3.7l1.9,2.6l2.7,0.5l1,2.7l-1.1,3.6l-2.1,1.6h-1.1l-0.8,3.9l0.5,2.9l3.2,4.4l1.6,5.3l1.5,4.7l1.3,3.1l3.4,5.8l1.5,2.6l0.5,2.9 l1.6,1v2.4l-0.8,1.9l-1.8,7.1l-0.5,1.9l2.4,2.7l4.2,0.5l4.5,1.8l3.9,2.1h2.9l2.9,3.1l2.6,4.8l1.1,2.3l3.9,2.1l4.9,0.8l1.5,2.1 l0.6,3.2l-1.5,0.6l0.3,1l3.2,0.8l2.7,0.2l3.2-1.7l3.9,4.2l0.8,2.3l2.6,4.2l0.3,3.2v9.4l0.5,1.8l10,1.5l19.7,2.7L144.7,382.2 L144.7,382.2z M56.6,338.5l1.3,1.5l-0.2,1.3l-3.2-0.1l-0.6-1.2l-0.6-1.5L56.6,338.5L56.6,338.5z M58.5,338.5l1.2-0.6l3.6,2.1 l3.1,1.2l-0.9,0.6l-4.5-0.2l-1.6-1.6L58.5,338.5L58.5,338.5z M79.2,358.3l1.8,2.3l0.8,1l1.5,0.6l0.6-1.5l-1-1.8l-2.7-2l-1.1,0.2 V358.3z M77.7,366.9l1.8,3.2l1.2,1.9l-1.5,0.2l-1.3-1.2c0,0-0.7-1.5-0.7-1.9s0-2.2,0-2.2L77.7,366.9L77.7,366.9z'
              className={
                'map-state' +
                (selectableStates.includes('California') ? ' selectable' : '') +
                (selectedState === 'California' ? ' selected' : '')
              }
              data-tooltiptext={tooltipData?.['California']?.text || null}
              data-tooltipheading={tooltipData?.['California']?.heading || null}
              onClick={() =>
                selectableStates.includes('California')
                  ? onStateClick('California')
                  : null
              }
            />
          </svg>
        </div>
      </tooltip.provider>
    </Styles>
  );
};

export default InteractiveMap;
